import { useEffect, useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { Check, Paperclip, X } from "lucide-react";
import { toast } from "sonner";
import { CONTACT_OPTIONS } from "@/data";

const EASE = [0.16, 1, 0.3, 1];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CONTACT_EMAIL = "enquiries@softwarefairgo.com.au";
const DOC_EXTS = ["pdf", "doc", "docx", "txt", "csv", "rtf", "odt", "xls", "xlsx", "ppt", "pptx"];
const MAX_FILE = 10 * 1024 * 1024;
const MAX_FILES = 5;
const ACCEPT = DOC_EXTS.map((e) => `.${e}`).join(",");

const EMPTY = {
    name: "",
    business: "",
    email: "",
    service: CONTACT_OPTIONS[0],
    details: "",
};

export default function Contact({ prefill }) {
    const [form, setForm] = useState(EMPTY);
    const [files, setFiles] = useState([]);
    const [sending, setSending] = useState(false);
    const [refCode, setRefCode] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        if (prefill?.value) {
            setForm((f) => ({ ...f, service: prefill.value }));
        }
    }, [prefill]);

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const onFiles = (e) => {
        setError("");
        const picked = Array.from(e.target.files || []).slice(0, MAX_FILES);
        const ok = [];
        const problems = [];
        for (const f of picked) {
            const ext = f.name.includes(".") ? f.name.split(".").pop().toLowerCase() : "";
            if (f.size > MAX_FILE) {
                problems.push(`${f.name} is over 10 MB`);
            } else if (!DOC_EXTS.includes(ext)) {
                problems.push(`${f.name} isn't a document (PDF/Word/Excel/CSV/text only)`);
            } else {
                ok.push(f);
            }
        }
        if (problems.length) setError(problems.join(" · "));
        setFiles(ok);
        e.target.value = "";
    };

    const removeFile = (name) =>
        setFiles((list) => list.filter((f) => f.name !== name));

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (!form.name.trim() || !form.email.trim()) {
            setError("Please add your name and email so we can reply.");
            return;
        }
        setSending(true);
        try {
            const res = await axios.post(`${API}/enquiries`, form);
            const id = res.data?.id || "";
            let uploadFailures = 0;
            for (const f of files) {
                try {
                    const fd = new FormData();
                    fd.append("file", f);
                    await axios.post(`${API}/enquiries/${id}/attachments`, fd);
                } catch (uploadErr) {
                    uploadFailures += 1;
                }
            }
            setRefCode(`SFG-${id.slice(-6).toUpperCase()}`);
            if (uploadFailures > 0) {
                toast.error(
                    `Enquiry saved, but ${uploadFailures} file${uploadFailures > 1 ? "s" : ""} failed to upload — we'll be in touch.`,
                );
            } else {
                toast.success(
                    "Enquiry received — we'll reply within one business day.",
                );
            }
        } catch (err) {
            toast.error(
                `Something went wrong. Please try again, or email ${CONTACT_EMAIL}.`,
            );
        } finally {
            setSending(false);
        }
    };

    const reset = () => {
        setRefCode(null);
        setForm(EMPTY);
        setFiles([]);
    };

    return (
        <section id="contact">
            <div className="wrap contact-grid">
                <div className="contact-info">
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ duration: 0.8, ease: EASE }}
                    >
                        <h2 data-testid="contact-heading">Reach us or Enquire Form</h2>
                        <p>
                            Tell us what you're considering, and roughly when
                            you need a decision. We'll come back with a scope
                            and price within one business day.
                        </p>
                        <div className="contact-detail">
                            <div>
                                <a
                                    href={`mailto:${CONTACT_EMAIL}`}
                                    data-testid="contact-email-link"
                                >
                                    {CONTACT_EMAIL}
                                </a>
                            </div>
                            <div>
                                <a
                                    href="tel:+61447862758"
                                    data-testid="contact-phone-link"
                                >
                                    +61 447 862 758
                                </a>
                            </div>
                            <div data-testid="clearance-note-contact">
                                All consultants hold Australian Government
                                Baseline clearance · NV1 available
                            </div>
                            <div>
                                Servicing Australia &amp; New Zealand — remote
                                by default
                            </div>
                        </div>
                    </motion.div>
                </div>

                <div className="contact-form-wrap">
                    <AnimatePresence mode="wait">
                        {refCode ? (
                            <motion.div
                                className="success-card"
                                key="success"
                                data-testid="contact-success-panel"
                                initial={{ opacity: 0, y: 24 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -16 }}
                                transition={{ duration: 0.6, ease: EASE }}
                            >
                                <div className="success-seal">
                                    <Check size={22} strokeWidth={1.5} />
                                </div>
                                <div className="success-ref">
                                    ENQUIRY {refCode}
                                </div>
                                <h3 className="success-title">
                                    Enquiry received.
                                </h3>
                                <p className="success-copy">
                                    Thanks
                                    {form.name
                                        ? `, ${form.name.split(" ")[0]}`
                                        : ""}{" "}
                                    — your request
                                    {files.length
                                        ? ` with ${files.length} attached file${files.length > 1 ? "s" : ""}`
                                        : ""}{" "}
                                    is with us. We'll come back with a scope
                                    and price within one business day. No
                                    obligation.
                                </p>
                                <button
                                    className="btn btn-ghost"
                                    data-testid="contact-send-another"
                                    onClick={reset}
                                >
                                    Send another enquiry
                                </button>
                            </motion.div>
                        ) : (
                            <motion.form
                                key="form"
                                onSubmit={submit}
                                initial={{ opacity: 0, y: 24 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -16 }}
                                transition={{ duration: 0.6, ease: EASE }}
                                data-testid="contact-form"
                            >
                                <div className="form-row">
                                    <div className="field">
                                        <label htmlFor="name">Your name</label>
                                        <input
                                            type="text"
                                            id="name"
                                            name="name"
                                            autoComplete="name"
                                            placeholder="Jordan Lee"
                                            value={form.name}
                                            onChange={set("name")}
                                            data-testid="contact-input-name"
                                        />
                                    </div>
                                    <div className="field">
                                        <label htmlFor="business">
                                            Business name
                                        </label>
                                        <input
                                            type="text"
                                            id="business"
                                            name="business"
                                            autoComplete="organization"
                                            placeholder="Your business"
                                            value={form.business}
                                            onChange={set("business")}
                                            data-testid="contact-input-business"
                                        />
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="field">
                                        <label htmlFor="email">Email</label>
                                        <input
                                            type="email"
                                            id="email"
                                            name="email"
                                            autoComplete="email"
                                            placeholder="you@business.com.au"
                                            value={form.email}
                                            onChange={set("email")}
                                            data-testid="contact-input-email"
                                        />
                                    </div>
                                    <div className="field">
                                        <label htmlFor="service">
                                            Interested in
                                        </label>
                                        <select
                                            id="service"
                                            name="service"
                                            value={form.service}
                                            onChange={set("service")}
                                            data-testid="contact-select-service"
                                        >
                                            {CONTACT_OPTIONS.map((opt) => (
                                                <option key={opt} value={opt}>
                                                    {opt}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="field">
                                    <label htmlFor="details">
                                        Tell us what you need
                                    </label>
                                    <textarea
                                        id="details"
                                        name="details"
                                        autoComplete="off"
                                        placeholder="e.g. Reviewing a proposal for a new POS system, decision needed by end of month."
                                        value={form.details}
                                        onChange={set("details")}
                                        data-testid="contact-input-details"
                                    />
                                </div>
                                <div
                                    className="field attach-drop"
                                    data-testid="contact-attachments"
                                >
                                    <label htmlFor="attachments">
                                        <Paperclip size={15} strokeWidth={1.5} />
                                        Attach documents — PDF, Word, Excel,
                                        CSV (max 10 MB each, up to{" "}
                                        {MAX_FILES} files, no images)
                                    </label>
                                    <input
                                        type="file"
                                        id="attachments"
                                        multiple
                                        accept={ACCEPT}
                                        autoComplete="off"
                                        onChange={onFiles}
                                        data-testid="contact-attachment-input"
                                    />
                                </div>
                                {files.length > 0 && (
                                    <div
                                        className="file-chips"
                                        data-testid="attachment-chips"
                                    >
                                        {files.map((f) => (
                                            <span
                                                className="file-chip"
                                                key={f.name}
                                            >
                                                <Paperclip
                                                    size={12}
                                                    strokeWidth={1.5}
                                                />{" "}
                                                {f.name}
                                                <button
                                                    type="button"
                                                    className="file-chip-x"
                                                    aria-label={`Remove ${f.name}`}
                                                    data-testid={`remove-attachment-${f.name}`}
                                                    onClick={() =>
                                                        removeFile(f.name)
                                                    }
                                                >
                                                    <X size={11} />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                                {error && (
                                    <div
                                        className="form-error"
                                        data-testid="contact-form-error"
                                    >
                                        {error}
                                    </div>
                                )}
                                <button
                                    type="submit"
                                    className="submit-btn"
                                    disabled={sending}
                                    data-testid="contact-submit-button"
                                >
                                    {sending
                                        ? "Sending…"
                                        : "Send enquiry"}
                                </button>
                                <div className="form-note">
                                    We reply within one business day. No
                                    obligation.
                                </div>
                            </motion.form>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </section>
    );
}
